import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import disbursementsAPI from '../../api/disbursementsAPI';
import chartOfAccountsAPI from '../../api/chartOfAccountsAPI';
import ChartOfAccountsSearch from '../../components/ChartOfAccountsSearch';
import MoneyInput from '../../components/MoneyInput';
import VendorSearch from '../../components/VendorSearch';
import DisbursementAuditTab from './DisbursementAuditTab';
import PredictiveMemoInput from '../../components/PredictiveMemoInput';
import { useVendorTerms } from '../../utils/terminology';

const EditDisbursementPage = () => {
  const term = useVendorTerms();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [disbursement, setDisbursement] = useState(null);
  const [accounts, setAccounts] = useState([]);

  // Dialog states
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [postLoading, setPostLoading] = useState(false);
  const [voidLoading, setVoidLoading] = useState(false);

  // UI tabs
  const [activeTab, setActiveTab] = useState('details');

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    memo: '',
    checkNo: '',
    orNumber: '',
    payeeType: 'vendor',
    vendorId: null,
    customerId: null,
    payeeSnapshot: { name: '', tin: '', address: {} },
    lines: [{ accountId: '', accountDisplay: '', debit: 0, credit: 0, description: '' }]
  });

  const isEditable = disbursement && disbursement.status === 'draft';
  const isVoided = disbursement && disbursement.status === 'voided';
  const canVoid = disbursement && disbursement.status === 'posted';
  const canPost = disbursement && disbursement.status === 'draft' && isBalanced();

  // Compute memo context for predictive input
  const memoContext = useMemo(() => {
    const accountIds = formData.lines
      .map(l => l.accountId)
      .filter(Boolean);
    const uniqueAccountIds = [...new Set(accountIds)];
    
    const accountNames = formData.lines
      .map(l => l.accountDisplay)
      .filter(Boolean)
      .slice(0, 2);
    
    // Determine vendor/customer info
    let vendorId = null;
    let vendorName = null;
    
    if (formData.payeeType === 'vendor' && formData.vendorId) {
      vendorId = formData.vendorId;
      vendorName = formData.payeeSnapshot?.name || null;
    } else if (formData.payeeType === 'customer' && formData.customerId) {
      vendorName = formData.payeeSnapshot?.name || null;
    }
    
    return {
      entryDate: formData.date,
      accounts: uniqueAccountIds,
      accountNames,
      vendorId,
      vendorName,
      entityType: 'disbursement'
    };
  }, [formData.lines, formData.date, formData.payeeType, formData.vendorId, formData.customerId, formData.payeeSnapshot]);

  function isBalanced() {
    const totalDebits = formData.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
    const totalCredits = formData.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
    return Math.abs(totalDebits - totalCredits) < 0.01;
  }

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInitialData = async () => {
    setLoading(true);
    setError('');
    try {
      const [accountsRes, disbursementRes] = await Promise.all([
        chartOfAccountsAPI.getAll(),
        disbursementsAPI.getById(id)
      ]);

      setAccounts(accountsRes.data || []);

      if (disbursementRes.data) {
        setDisbursement(disbursementRes.data);

        setFormData({
          date: disbursementRes.data.date?.split('T')[0] || '',
          memo: disbursementRes.data.memo || '',
          checkNo: disbursementRes.data.checkNo || '',
          orNumber: disbursementRes.data.orNumber || '',
          payeeType: disbursementRes.data.payeeType || 'vendor',
          vendorId: disbursementRes.data.vendorId || null,
          customerId: disbursementRes.data.customerId || null,
          payeeSnapshot: disbursementRes.data.payeeSnapshot || { name: '', tin: '', address: {} },
          lines: (disbursementRes.data.lines || []).map(line => ({
            _id: line._id,
            accountId: line.accountId?._id || line.accountId || '',
            accountDisplay: line.accountId?.accountNumber
              ? `${line.accountId.accountNumber} - ${line.accountId.accountName}`
              : '',
            debit: line.debit || 0,
            credit: line.credit || 0,
            description: line.description || ''
          }))
        });
      }
    } catch (err) {
      setError(err.message || 'Failed to load disbursement');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'payeeType' ? value : value
    }));
  };

  const handlePayeeSnapshotChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      payeeSnapshot: {
        ...prev.payeeSnapshot,
        [field]: value
      }
    }));
  };

  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      payeeSnapshot: {
        ...prev.payeeSnapshot,
        address: {
          ...prev.payeeSnapshot.address,
          [field]: value
        }
      }
    }));
  };

  const handleLineChange = (index, field, value) => {
    setFormData(prev => {
      const newLines = [...prev.lines];
      newLines[index] = {
        ...newLines[index],
        [field]: value
      };
      return { ...prev, lines: newLines };
    });
  };

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { accountId: '', accountDisplay: '', debit: 0, credit: 0, description: '' }]
    }));
  };

  const removeLine = (index) => {
    if (formData.lines.length > 1) {
      setFormData(prev => ({
        ...prev,
        lines: prev.lines.filter((_, i) => i !== index)
      }));
    }
  };

  const handleChartOfAccountSelect = (account, index) => {
    if (!account) {
      setFormData(prev => {
        const newLines = [...prev.lines];
        newLines[index] = {
          ...newLines[index],
          accountId: '',
          accountDisplay: ''
        };
        return { ...prev, lines: newLines };
      });
      return;
    }
    setFormData(prev => {
      const newLines = [...prev.lines];
      newLines[index] = {
        ...newLines[index],
        accountId: account._id,
        accountDisplay: `${account.accountNumber} - ${account.accountName}`
      };
      return { ...prev, lines: newLines };
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        date: formData.date,
        memo: formData.memo,
        checkNo: formData.checkNo,
        orNumber: formData.orNumber,
        payeeType: formData.payeeType,
        vendorId: formData.payeeType === 'vendor' ? formData.vendorId : null,
        customerId: formData.payeeType === 'customer' ? formData.customerId : null,
        payeeSnapshot: formData.payeeType === 'vendor' ? undefined : formData.payeeSnapshot,
        lines: formData.lines.map((line) => ({
          _id: line._id,
          accountId: line.accountId,
          debit: line.debit,
          credit: line.credit,
          description: line.description
        }))
      };

      await disbursementsAPI.update(disbursement._id, payload);

      setSuccess('Disbursement updated successfully');
      setTimeout(() => fetchInitialData(), 500);
    } catch (err) {
      setError(err.message || 'Failed to save disbursement');
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    if (!isBalanced()) {
      setError('Disbursement must be balanced before posting');
      return;
    }

    setPostLoading(true);
    setError('');
    try {
      await disbursementsAPI.post(disbursement._id);
      setSuccess('Disbursement posted successfully');
      
      // Refresh data to get updated status
      await fetchInitialData();
      
      // Open print in new tab
      setTimeout(() => {
        window.open(`/disbursements/${disbursement._id}/print`, '_blank', 'noopener,noreferrer');
      }, 500);
    } catch (err) {
      setError(err.message || 'Failed to post disbursement');
    } finally {
      setPostLoading(false);
    }
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) {
      setError('Void reason is required');
      return;
    }

    setVoidLoading(true);
    setError('');
    try {
      await disbursementsAPI.void(disbursement._id, { reason: voidReason });
      setSuccess('Disbursement voided successfully');
      setVoidModalOpen(false);
      setVoidReason('');
      setTimeout(() => fetchInitialData(), 500);
    } catch (err) {
      setError(err.message || 'Failed to void disbursement');
    } finally {
      setVoidLoading(false);
    }
  };

  const getTotalDebits = () => {
    return formData.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
  };

  const getTotalCredits = () => {
    return formData.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const linkedJournalEntryId = disbursement?.journalEntryId?._id || disbursement?.journalEntryId || null;
  const linkedJournalEntryNumber = disbursement?.journalEntryId?.entryNumber || null;

  if (loading && !disbursement) {
    return (
      <Layout>
        <div className="p-6 text-center text-gray-500">Loading...</div>
      </Layout>
    );
  }

  if (!disbursement) {
    return (
      <Layout>
        <div className="p-6 text-center text-red-500">Disbursement not found</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-6xl mx-auto">
          {/* Page Title */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold">
              <a href="/disbursements" className="text-teal-600 hover:underline">Disbursements</a> / Edit
            </h1>
          </div>

          {/* Header row */}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
            <div className="flex items-center gap-3">
              <span
                className={`px-2 py-1 rounded-full text-xs font-semibold capitalize ${
                  disbursement.status === 'draft'
                    ? 'bg-gray-100 text-gray-800'
                    : disbursement.status === 'posted'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {disbursement.status}
              </span>
              <span className="font-mono text-sm text-gray-600">{disbursement.referenceNo}</span>

              {linkedJournalEntryId && (
                <button
                  type="button"
                  onClick={() => navigate(`/journal-entry/${linkedJournalEntryId}/edit`)}
                  className="text-sm text-teal-600 hover:underline"
                  title="Open linked journal entry"
                >
                  Posted to JE# {linkedJournalEntryNumber || 'View'}
                </button>
              )}
            </div>
          </div>

          {/* Notifications */}
          {!isEditable && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
              <strong className="font-bold">View Only: </strong>
              This disbursement is {disbursement.status} and cannot be modified.
              {disbursement.status === 'posted' && ' To make changes, create a reversing entry.'}
              {disbursement.status === 'voided' && ' This disbursement has been voided.'}
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <div className="flex justify-between items-start">
                <div>
                  <strong className="font-bold">Error: </strong>
                  <span className="block sm:inline">{error}</span>
                </div>
                <button onClick={() => setError('')} className="ml-4 text-red-900 hover:text-red-600 text-lg">
                  ×
                </button>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              <div className="flex justify-between items-start">
                <div>
                  <strong className="font-bold">Success: </strong>
                  <span className="block sm:inline">{success}</span>
                </div>
                <button onClick={() => setSuccess('')} className="ml-4 text-green-900 hover:text-green-600 text-lg">
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Persistent balance warning */}
          {isEditable && !isBalanced() && (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
              Disbursement is out of balance by ₱{Math.abs(getTotalDebits() - getTotalCredits()).toFixed(2)}
            </div>
          )}

          {/* Tab navigation */}
          <div className="flex gap-8 border-b mb-4 overflow-x-auto whitespace-nowrap">
            <button
              className={`pb-2 ${
                activeTab === 'details' ? 'border-b-2 border-teal-600 font-semibold' : 'text-gray-600'
              }`}
              onClick={() => setActiveTab('details')}
              type="button"
            >
              DETAILS
            </button>

            <button
              className={`pb-2 ${
                activeTab === 'audit' ? 'border-b-2 border-teal-600 font-semibold' : 'text-gray-600'
              }`}
              onClick={() => setActiveTab('audit')}
              type="button"
            >
              AUDIT TRAIL
            </button>
          </div>

          {/* Tab content container */}
          <div className="bg-white rounded-lg shadow-md">
            {activeTab === 'details' && (
              <div className="p-6">
                {/* Voucher Header */}
                <div className="mb-6">
                  <h2 className="text-xl font-semibold mb-4">Voucher Header</h2>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">Reference No</label>
                      <input
                        type="text"
                        value={disbursement.referenceNo || ''}
                        className="w-full border rounded px-3 py-2 bg-gray-100 text-gray-600"
                        disabled
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">Date *</label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleFieldChange}
                        className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          !isEditable ? 'bg-gray-100 text-gray-600' : ''
                        }`}
                        required
                        disabled={loading || !isEditable}
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">Particulars *</label>
                      <PredictiveMemoInput
                        value={formData.memo}
                        onChange={(memo) => isEditable && setFormData(prev => ({ ...prev, memo }))}
                        disabled={loading || !isEditable}
                        required
                        placeholder="Required"
                        accountingClientId={disbursement?.accountingClient?._id || disbursement?.accountingClient}
                        context={memoContext}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">Check No</label>
                      <input
                        type="text"
                        name="checkNo"
                        value={formData.checkNo}
                        onChange={handleFieldChange}
                        className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          !isEditable ? 'bg-gray-100 text-gray-600' : ''
                        }`}
                        disabled={loading || !isEditable}
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">OR Number</label>
                      <input
                        type="text"
                        name="orNumber"
                        value={formData.orNumber}
                        onChange={handleFieldChange}
                        className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          !isEditable ? 'bg-gray-100 text-gray-600' : ''
                        }`}
                        disabled={loading || !isEditable}
                      />
                    </div>
                  </div>

                  {/* PAYEE INFORMATION - NESTED UNDER HEADER */}
                  <div className="border-t pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Payee Information</h3>

                    <div className="mb-3">
                      <label className="block text-gray-600 text-xs font-medium mb-1">Payee Type *</label>
                      <select
                        name="payeeType"
                        value={formData.payeeType}
                        onChange={handleFieldChange}
                        className={`w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          !isEditable ? 'bg-gray-100 text-gray-600' : ''
                        }`}
                        disabled={loading || !isEditable}
                      >
                        <option value="vendor">{term.one}</option>
                        <option value="customer">Customer</option>
                        <option value="employee">Employee</option>
                        <option value="government">Government</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {formData.payeeType === 'vendor' && (
                      <div className="mb-3">
                        <label className="block text-gray-600 text-xs font-medium mb-1">{term.one}</label>
                        <VendorSearch
                          value={formData.vendorId}
                          onSelect={(vendor) => setFormData(prev => ({ ...prev, vendorId: vendor }))}
                          disabled={loading || !isEditable}
                        />
                      </div>
                    )}

                    {!['vendor', 'customer'].includes(formData.payeeType) && (
                      <div className="space-y-2">
                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-1">Payee Name *</label>
                          <input
                            type="text"
                            value={formData.payeeSnapshot.name}
                            onChange={(e) => handlePayeeSnapshotChange('name', e.target.value)}
                            className={`w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                              !isEditable ? 'bg-gray-100 text-gray-600' : ''
                            }`}
                            required
                            disabled={loading || !isEditable}
                          />
                        </div>

                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-1">TIN (optional)</label>
                          <input
                            type="text"
                            value={formData.payeeSnapshot.tin || ''}
                            onChange={(e) => handlePayeeSnapshotChange('tin', e.target.value)}
                            className={`w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                              !isEditable ? 'bg-gray-100 text-gray-600' : ''
                            }`}
                            disabled={loading || !isEditable}
                          />
                        </div>

                        <div>
                          <label className="block text-gray-600 text-xs font-medium mb-1">Address</label>
                          <input
                            type="text"
                            placeholder="Street"
                            value={formData.payeeSnapshot.address?.street || ''}
                            onChange={(e) => handleAddressChange('street', e.target.value)}
                            className={`w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 mb-2 ${
                              !isEditable ? 'bg-gray-100 text-gray-600' : ''
                            }`}
                            disabled={loading || !isEditable}
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              placeholder="City"
                              value={formData.payeeSnapshot.address?.city || ''}
                              onChange={(e) => handleAddressChange('city', e.target.value)}
                              className={`border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                !isEditable ? 'bg-gray-100 text-gray-600' : ''
                              }`}
                              disabled={loading || !isEditable}
                            />
                            <input
                              type="text"
                              placeholder="Province"
                              value={formData.payeeSnapshot.address?.province || ''}
                              onChange={(e) => handleAddressChange('province', e.target.value)}
                              className={`border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                !isEditable ? 'bg-gray-100 text-gray-600' : ''
                              }`}
                              disabled={loading || !isEditable}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Entry Lines */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">Entry Lines</h2>
                    {isEditable && (
                      <button
                        type="button"
                        onClick={addLine}
                        className="px-3 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 flex items-center text-sm"
                        disabled={loading}
                      >
                        <span className="mr-1">+</span> Add Line
                      </button>
                    )}
                  </div>

                  <div className="overflow-x-auto min-h-96">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Account *
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Debit
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Credit
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {formData.lines.map((line, index) => (
                          <tr key={index} className="hover:bg-gray-50 align-top">
                            <td className="px-4 py-3">
                              {isEditable ? (
                                <ChartOfAccountsSearch
                                  value={line.accountDisplay || ''}
                                  onChange={(value) => handleLineChange(index, 'accountDisplay', value)}
                                  onSelect={(account) => handleChartOfAccountSelect(account, index)}
                                  placeholder="Search chart of accounts..."
                                  showLinkedBadge={false}
                                  disabled={loading}
                                  leafOnly={true}
                                />
                              ) : (
                                <div className="px-2 py-1 text-sm">
                                  {(() => {
                                    const found = accounts.find((a) => a._id === line.accountId);
                                    return found ? `${found.accountNumber} - ${found.accountName}` : "-";
                                  })()}
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-3 text-right">
                              <MoneyInput
                                value={line.debit}
                                onValueChange={(value) => handleLineChange(index, 'debit', value)}
                                disabled={loading || !isEditable}
                              />
                            </td>

                            <td className="px-4 py-3 text-right">
                              <MoneyInput
                                value={line.credit}
                                onValueChange={(value) => handleLineChange(index, 'credit', value)}
                                disabled={loading || !isEditable}
                              />
                            </td>

                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={line.description}
                                onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                                className={`w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                                  !isEditable ? 'bg-gray-100 text-gray-600' : ''
                                }`}
                                placeholder="Optional description"
                                disabled={loading || !isEditable}
                              />
                            </td>

                            <td className="px-4 py-3 text-center whitespace-nowrap">
                              {isEditable && (
                                <button
                                  type="button"
                                  onClick={() => removeLine(index)}
                                  className="text-red-600 hover:text-red-900 text-sm font-medium"
                                  disabled={formData.lines.length === 1 || loading}
                                >
                                  Remove
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Totals */}
                  <div className="border-t pt-4 mt-4">
                    <div className="flex justify-end items-center space-x-8">
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Total Debits</div>
                        <div className="text-lg font-semibold">{formatCurrency(getTotalDebits())}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Total Credits</div>
                        <div className="text-lg font-semibold">{formatCurrency(getTotalCredits())}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Difference</div>
                        <div className={`text-lg font-semibold ${isBalanced() ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(Math.abs(getTotalDebits() - getTotalCredits()))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {(isEditable || canVoid || disbursement.status === 'posted') && (
                  <div className="border-t pt-6 flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => navigate('/disbursements')}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50"
                      disabled={loading}
                    >
                      Back to List
                    </button>

                    {isEditable && (
                      <>
                        <button
                          onClick={handleSave}
                          disabled={loading}
                          className="px-6 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition disabled:opacity-50"
                        >
                          {loading ? 'Saving...' : 'Save Draft'}
                        </button>

                        <button
                          onClick={handlePost}
                          disabled={!canPost || postLoading}
                          title={!isBalanced() ? 'Disbursement must be balanced' : ''}
                          className="px-6 py-2 bg-teal-600 text-white rounded hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {postLoading ? 'Posting...' : 'Post'}
                        </button>
                      </>
                    )}

                    {canVoid && (
                      <button
                        onClick={() => setVoidModalOpen(true)}
                        className="px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                      >
                        Void
                      </button>
                    )}

                    {disbursement.status === 'posted' && (
                      <button
                        onClick={() => window.open(`/disbursements/${disbursement._id}/print`, '_blank', 'noopener,noreferrer')}
                        className="px-6 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 transition"
                      >
                        Print
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Audit Log Tab */}
            {activeTab === 'audit' && disbursement && (
              <div className="p-6">
                <DisbursementAuditTab disbursementId={disbursement._id} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Void Modal */}
      {voidModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Void Disbursement</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Void Reason (required)</label>
              <textarea
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                rows="4"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setVoidModalOpen(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleVoid}
                disabled={voidLoading || !voidReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {voidLoading ? 'Voiding...' : 'Void'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default EditDisbursementPage;
